// src/components/HeroSection.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import AboutImageSlideshow from './AboutImageSlideshow';

function HeroSection({ isAdmin, onLogout }) {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Handle window resize for responsive design
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div style={heroStyles.container}>
      {/* 2-Image Grid Background */}
      <div style={heroStyles.imageGrid}>
        {/* Left Side */}
        <div style={heroStyles.imageSlot}>
          <AboutImageSlideshow
            section="hero-left"
            isAdmin={isAdmin}
            customImageStyle={heroStyles.gridImage}
            customContainerStyle={heroStyles.gridImageContainer}
          />
          
          {/* Left Side Content */}
          <div style={heroStyles.leftContent}>
            <p style={isMobile ? heroStyles.leftSubtitleMobile : heroStyles.leftSubtitle}>
              Prenájom, kúpa a predaj nehnuteľností a áut
            </p>
            
            <div style={isMobile ? heroStyles.leftButtonWrapperMobile : heroStyles.leftButtonWrapper}>
              <Link to="/nehnutelnosti" style={isMobile ? heroStyles.buttonLinkMobile : heroStyles.buttonLink}>
                <button className="heroButton heroButtonWhite">
                  Ponuka nehnuteľností
                </button>
              </Link>
              
              <Link to="/auta" style={isMobile ? heroStyles.buttonLinkMobile : heroStyles.buttonLink}>
                <button className="heroButton heroButtonBlue">
                  Ponuka áut
                </button>
              </Link>
            </div>
          </div>
        </div>
        
        {/* Right Side */}
        <div style={heroStyles.imageSlot}>
          <AboutImageSlideshow
            section="hero-right"
            isAdmin={isAdmin}
            customImageStyle={heroStyles.gridImage}
            customContainerStyle={heroStyles.gridImageContainer}
          />
        </div>
      </div>
      
      {/* Dark Overlay for content readability */}
      <div style={heroStyles.overlay}></div>

      {/* 
        We add a <style> block for the hover animations, 
        so we can use :hover pseudo-classes. 
      */}
      <style>{`
        .heroButton {
          font-size: 0.7rem;
          padding: 0.4rem 0.8rem;
          border-radius: 0.3rem;
          opacity: 0.9;
          border: none;
          cursor: pointer;
          transition: transform 0.3s ease, background-color 0.3s ease;
          margin: 0.25rem;
          width: auto;
        }
        .heroButton:hover {
          transform: scale(1.05);
        }

        /* Blue button */
        .heroButtonBlue {
          background-color: #2c69a0;
          color: #fff;
        }
        .heroButtonBlue:hover {
          background-color: #22507a;
        }

        /* White button */
        .heroButtonWhite {
          background-color: #fff;
          color: #000;
        }
        .heroButtonWhite:hover {
          background-color: #f5f5f5;
        }
        
        /* Responsive styles */
        @media (max-width: 768px) {
          .heroButton {
            font-size: 0.7rem;
            padding: 0.4rem 0.6rem;
            width: 100%;
            margin: 0.25rem 0;
          }
        }
      `}</style>
    </div>
  );
}

export default HeroSection;

const heroStyles = {
  container: {
    fontFamily: "'Inter', Helvetica, Arial, sans-serif",
    position: 'relative',
    width: '100%',
    height: '33vh',
    minHeight: '200px',
    color: '#fff',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    overflowX: 'hidden',
    maxWidth: '100vw',
  },
  imageGrid: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    display: 'flex',
  },
  imageSlot: {
    flex: 1,
    position: 'relative',
  },
  gridImageContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
  },
  gridImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 1,
  },
  
  // Left Side Content
  leftContent: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    textAlign: 'center',
    width: '90%',
    maxWidth: '400px',
    padding: '0 15px',
    boxSizing: 'border-box',
    zIndex: 2,
  },
  leftSubtitle: {
    fontSize: '0.9rem',
    marginBottom: '1rem',
  },
  leftSubtitleMobile: {
    fontSize: '0.8rem',
    marginBottom: '0.8rem',
    padding: '0 10px',
  },
  leftButtonWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.3rem',
    justifyContent: 'center',
  },
  leftButtonWrapperMobile: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.3rem',
    width: '100%',
  },
  
  // Right Side Content
  rightContent: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    textAlign: 'center',
    width: '90%',
    maxWidth: '400px',
    padding: '0 15px',
    boxSizing: 'border-box',
    zIndex: 2,
  },
  rightTitle: {
    fontSize: '2.5rem',
    fontWeight: 'bold',
    marginBottom: '1rem',
    textShadow: '2px 2px 4px rgba(0, 0, 0, 0.5)',
  },
  rightTitleMobile: {
    fontSize: '1.8rem',
    fontWeight: 'bold',
    marginBottom: '0.8rem',
    textShadow: '2px 2px 4px rgba(0, 0, 0, 0.5)',
  },
  rightSubtitle: {
    fontSize: '0.9rem',
    marginBottom: '1.5rem',
    textShadow: '1px 1px 2px rgba(0, 0, 0, 0.5)',
    fontWeight: '500',
  },
  rightSubtitleMobile: {
    fontSize: '0.8rem',
    marginBottom: '1rem',
    textShadow: '1px 1px 2px rgba(0, 0, 0, 0.5)',
    padding: '0 10px',
    fontWeight: '500',
  },
  rightButtonWrapper: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  rightButtonWrapperMobile: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.3rem',
    width: '100%',
  },
  
  buttonLink: {
    textDecoration: 'none',
  },
  buttonLinkMobile: {
    textDecoration: 'none',
    width: '100%',
  },
};
